import React, { Component, useState } from "react";
import { Pagination } from "@culturehq/components";
import client from "@culturehq/client";

import { useRecognitionCreatedListener } from "app/hooks";
import { errorService, seo, t } from "app/utils";

import { QueryStringProvider, QueryStringConsumer } from "app/context/QueryStringContext";

import ErrorBoundary from "components/common/ErrorBoundary";
import FeedItemPlaceholder from "components/feed/FeedItemPlaceholder";

import Recognition from "./Recognition";
import RecognitionFilters from "./RecognitionFilters";
import NoRecognitions from "./NoRecognitions";

import TrendingValues from "./side-panels/TrendingValues";
import TopRecognizers from "./side-panels/TopRecognizers";
import MostRecognized from "./side-panels/MostRecognized";

const NewContentRecognitions = ({ count, onNewContentLoad }) => (
  <div className="NewContent box-shadow" onClick={onNewContentLoad}>
    <p>
      {count === 1
        ? t("See 1 new recognition")
        : t(`See ${count} new recognitions`)}
    </p>
  </div>
);

const UnloadedRecognitions = () => (
  <>
    <FeedItemPlaceholder />
    <FeedItemPlaceholder />
    <FeedItemPlaceholder />
  </>
);

const RecognitionList = ({ recognitions, pagination, loading, onPageSelected }) => {
  if (loading) {
    return <UnloadedRecognitions />;
  }

  if (!recognitions.length) {
    return <NoRecognitions />;
  }

  return (
    <>
      {recognitions.map(recognition => (
        <Recognition key={recognition.id} recognition={recognition} />
      ))}
      <Pagination {...pagination} onClick={onPageSelected} />
    </>
  );
};

const fromOptions = ["automatic", "manual"];

const queryToOptions = queryString => ({
  departmentIds: queryString.d || null,
  locationIds: queryString.l || null,
  type: fromOptions.includes(queryString.f) ? queryString.f : null,
  organizationValueIds: queryString.v || null,
  recognitionTypeIds: queryString.t || null
});

const queryStringParsers = {
  d: "intList",
  f: "string",
  l: "intList",
  t: "intList",
  v: "intList"
};

class RecognitionsContext extends Component {
  constructor(props) {
    super(props);

    this.state = {
      recognitions: [],
      newContent: [],
      pagination: null,
      page: 1,
      loading: true,
      options: queryToOptions(props.queryString)
    };
  }

  componentDidMount() {
    this.fetchRecognitions();
    seo({
      title: `${t("Recognitions")} - CultureHQ.com`
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { listenerRecognition, queryString } = this.props;
    const { newContent, options, page, recognitions } = this.state;

    if (options !== prevState.options || page !== prevState.page) {
      this.fetchRecognitions();
    }

    if (queryString !== prevProps.queryString) {
      this.setState({ options: queryToOptions(queryString), page: 1 });
    }

    if (listenerRecognition !== prevProps.listenerRecognition) {
      if (recognitions.concat(newContent).every(({ id }) => id !== listenerRecognition.id)) {
        this.setState(({ newContent: current }) => ({
          newContent: [listenerRecognition].concat(current)
        }));
      }
    }
  }

  handleCreate = recognition => {
    this.setState(({ recognitions }) => ({
      recognitions: [recognition].concat(recognitions)
    }));
  };

  handlePageSelected = page => {
    this.setState({ page });
  };

  handleNewContentLoad = () => {
    this.setState(({ recognitions, newContent }) => ({
      recognitions: [...newContent, ...recognitions],
      newContent: []
    }));
  };

  async fetchRecognitions() {
    this.setState({ loading: true });
    const { page, options } = this.state;

    try {
      const { recognitions, pagination } = await client.listRecognitions({
        ...options,
        page
      });

      this.setState({ recognitions, pagination, loading: false });
    } catch (error) {
      errorService.error(error);
      this.setState({ loading: false });
    }
  }

  render() {
    const { recognitions, options, pagination, newContent, loading } = this.state;

    return (
      <div className="Recognitions">
        <section role="complementary" aria-label={t("Left bar - Filters")}>
          <div className="Filters SidePanel col-sm-12 col-sm-8 col-md-9 col-lg-2">
            <RecognitionFilters
              loading={loading}
              options={options}
              onCreate={this.handleCreate}
              pagination={pagination}
            />
          </div>
        </section>
        <section role="main">
          <div className="Central--column col-xs-12 col-sm-8 col-md-9 col-lg-7">
            <ErrorBoundary>
              {newContent.length > 0 && (
                <NewContentRecognitions
                  count={newContent.length}
                  onNewContentLoad={this.handleNewContentLoad}
                />
              )}
              <RecognitionList
                recognitions={recognitions}
                pagination={pagination}
                loading={loading}
                onPageSelected={this.handlePageSelected}
              />
            </ErrorBoundary>
          </div>
        </section>
        <section role="complementary" aria-label={t("Right bar")}>
          <div className="recognitions__right-column hidden-xs col-sm-4 col-md-3 col-lg-3">
            <TrendingValues />
            <TopRecognizers />
            <MostRecognized />
          </div>
        </section>
      </div>
    );
  }
}

const Recognitions = () => {
  const [listenerRecognition, setListenerRecognition] = useState({});
  useRecognitionCreatedListener(setListenerRecognition);

  return (
    <QueryStringProvider parsers={queryStringParsers}>
      <QueryStringConsumer>
        {queryStringState => (
          <RecognitionsContext
            {...queryStringState}
            listenerRecognition={listenerRecognition}
          />
        )}
      </QueryStringConsumer>
    </QueryStringProvider>
  );
};

export default Recognitions;
